<template>
    <div class="card card-gray mt-3 p-3">
        <div class="row align-items-center">
            <div class="col-9 pe-2">
                <div class="input-group">
                    <span class="input-group-text input-part-stair-icon input-group-icon my-2" :class="{'is-invalid': isNameInvalid()}">
                        <StairsIcon class="text-primary"/>
                    </span>
                    <input type="text"
                           class="form-control my-2"
                           :class="{'is-invalid': isNameInvalid()}"
                           :value="state.name"
                           @change="updateName($event.target.value)"
                           :placeholder="$t('form.floorArea.buildingPartName')"
                    >
                </div>
                <div class="invalid-feedback d-block" v-if="isNameInvalid()">
                    {{ $t('form.errors.buildingPartNotFilled') }}
                </div>
            </div>
            <div class="col-3 pe-0 form-height">
                <button class="btn btn-outline-secondary"
                        :aria-label="$t('form.floorArea.buttonCloneBuildingPart')"
                        :title="$t('form.floorArea.buttonCloneBuildingPart')"
                        @click="$emit('clone')"
                >
                    <span class="fas fa-clone"></span>
                </button>
                <button @click="$emit('remove')"
                        class="btn btn-outline-secondary ms-2"
                >
                    <span class="fas fa-times"></span>
                </button>
            </div>
        </div>
        <div>
            <template v-if="hasRooms()">
                <h5 class="mt-3">{{ $t('form.floorArea.rooms') }}{{  state.name.length !== 0 ? ' – ' + state.name : '' }}</h5>
                <p class="small">
                    {{ $t('form.floorArea.roomHelp') }}
                </p>
            </template>
            <div v-for="(_, index) in state.rooms" :key="index" class="mb-4">
                <RoomControl :ref="index"
                             :state="state.rooms[index]"
                             :roomLists="roomLists"
                             :validate="validate && state.rooms[index].value === null"
                             @remove="removeRoom(index)"
                             @clone="cloneRoom(index)"
                />
            </div>
            <div class="mt-3">
                <div class="row">
                    <div>
                        <button class="btn btn-outline-primary" @click="addRoom()">
                            <DoorIcon class="input-icon"/>
                            {{ $t('form.floorArea.buttonAddRoom') }}
                            {{  state.name.length !== 0 ? ' – ' + state.name : '' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {DoorIcon, StairsIcon} from '@/icons';
import {BuildingPartControlState} from '@/model/state/BuildingPartControlState';
import {computed, defineComponent, PropType} from 'vue';
import RoomControl from './RoomControl.vue';
import {RoomList} from './values';

export default defineComponent({
    components: {DoorIcon, RoomControl, StairsIcon},
    props: {
        roomLists: {
            type: Object as PropType<{ [listId: string]: RoomList }>,
            required: true,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        state: {
            type: Object as PropType<BuildingPartControlState>,
            required: true,
        },
    },
    emits: ['clone', 'remove'],
    setup(props) {
        const invalid = computed((): boolean => {
            if (!props.validate) {
                return false;
            }

            return props.state.value === null
                || Object.values(props.state.rooms).filter(rs => rs.value === null).length !== 0;
        });

        function addRoom(): void {
            props.state.addRoom();
        }

        function cloneRoom(index: string): void {
            props.state.cloneRoom(index);
        }

        function removeRoom(index: string): void {
            props.state.removeRoom(index);
        }

        function hasRooms(): boolean {
            return Object.keys(props.state.rooms).length > 0;
        }

        function isNameInvalid(): boolean {
            return invalid.value && props.state.name === '';
        }

        function updateName(name: string): void {
            props.state.updateName(name);
        }

        return {
            invalid,
            addRoom,
            cloneRoom,
            removeRoom,
            hasRooms,
            isNameInvalid,
            updateName,
        };
    },
});
</script>
